import { action, set, setProperties } from '@ember/object';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { type Task, task } from 'ember-concurrency';
import type { Insightpagesfavourite } from 'frontend-utils/models';
import {
  RI_BREAKDOWNS,
  type RiBreakdown,
} from 'frontend-utils/utils/constants/constant-values';
import {
  type DemoModeObject,
  getDemoModeCookie,
} from 'frontend-utils/utils/hash';
import type MarketRevenueInsightController from 'lumen/controllers/market/revenue-insight';
import type {
  Currency,
  RevenueInsightSegmentationValue,
  ReviExchangeRate,
  ReviMultiPropertyApiResponse,
  ReviRemainingElement,
  ReviRemainingElementsResponse,
} from 'lumen/models';
import type { SegmentationValueLike } from 'lumen/models/revenue-insight/segmentation-value';
import type { ReviApiResponseLike } from 'lumen/models/revi-api-response';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import {
  DEFAULT_SEGMENTATION_BLOCKS_ORDER,
  INVALID_RESERVATIONS_MESSAGE,
  KPI_CATEGORIES,
  type KpiCategory,
  MAX_AMOUNT_OF_BLOCKS,
  type ReferenceOption,
} from 'lumen/utils/constants/ri-constant-values';
import { deepExtend } from 'lumen/utils/extend-vanilla-js';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import getDateRange from 'lumen/utils/revenue-insight/get-date-range';
import getExtractDate from 'lumen/utils/revenue-insight/get-extract-date';
import handleRequestError from 'lumen/utils/revenue-insight/route-utils';
import { segmentationValueFormatter } from 'lumen/utils/revenue-insight/segmentation-value-formatter';
import {
  addDummyElementToFilteredSegmentationDict,
  getSegmentationIdValidations,
  type SegmentationDict,
} from 'lumen/utils/routes/hotel/revenue-insight';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';
import { timeout } from 'lumen/utils/waiters';
import { hash } from 'rsvp';

type StoredUnfilteredResponse = {
  cachedResponse: ReviApiResponseLike;
  /**
   * Only set in multi property
   */
  exchangeRates?: ReviExchangeRate[];
  hotelInfoId: string;
  /**
   * Only set in multi property
   */
  remainingElements?: ReviRemainingElementsResponse;
};

type QueryParams = {
  [key: string]: unknown;
  currency: unknown;
  fromDate: unknown;
  hidePickupLY: unknown;
  kpiCategory: unknown;
  reference: unknown;
  segmentationFilters: unknown;
  showFullPortfolio: unknown;
  stayDateFilter: unknown;
  toDate: unknown;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function compareQueryParameters(from: QueryParams, to: QueryParams) {
  return {
    currency: from.currency === to.currency,
    fromDate: from.fromDate === to.fromDate,
    hidePickupLY: from.hidePickupLY === to.hidePickupLY,
    kpiCategory: from.kpiCategory === to.kpiCategory,
    reference: from.reference === to.reference,
    segmentationFilters: from.segmentationFilters === to.segmentationFilters,
    showFullPortfolio: from.showFullPortfolio === to.showFullPortfolio,
    stayDateFilter: from.stayDateFilter === to.stayDateFilter,
    toDate: from.toDate === to.toDate,
  };
}

type Segmentations = Record<RiBreakdown, SegmentationValueLike[]> & {
  isLoading: boolean;
};

type Model = {
  // @ts-expect-error: Incorrect type
  currenciesTask: Task<string, unknown>;
  modelTask: Task<void, [Model, Params]>;
  segmentations: Segmentations;
};

type Params = {
  extractDate?: string;
  fromDate: string | null;
  hidePickupLY: boolean;
  kpiCategory: KpiCategory;
  reference: ReferenceOption;
  segmentationFilters: string[];
  showFullPortfolio: boolean;
  stayDateFilter: string;
  toDate: string | null;
};

export default class MarketRevenueInsightRoute extends Route {
  queryParams = {
    kpi: {
      refreshModel: false,
    },
    kpiCategory: {
      refreshModel: true,
    },
    roomsTab: {
      refreshModel: false,
    },
    revenueTab: {
      refreshModel: false,
    },
    extractDate: {
      refreshModel: true,
    },
    fromDate: {
      refreshModel: true,
    },
    toDate: {
      refreshModel: true,
    },
    stayDateFilter: {
      refreshModel: true,
    },
    segmentationFilters: {
      refreshModel: true,
    },
    showFullPortfolio: {
      refreshModel: true,
    },
    sortHotelsBy: {
      refreshModel: false,
    },
    sortHotelsAscending: {
      refreshModel: false,
    },
    hidePickupLY: {
      refreshModel: true,
    },
    stayDateAggregation: {
      refreshModel: false, // Since we do the aggregation in the frontend, the model doesn't need to be refreshed
    },
    refFromDate: {
      refreshModel: true,
    },
    reference: {
      refreshModel: true,
    },
    currency: {
      refreshModel: true,
    },
  };

  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  cachedMarketCurrencyArgs: {
    currency?: unknown;
    marketId?: string;
  } | null = null;
  favourites: Insightpagesfavourite[] | null = null;
  filteredSegmentationDict: SegmentationDict | null = {};
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  lastDisplayedData: unknown | null = null;
  storedUnfilteredResponse: StoredUnfilteredResponse | null = null;
  unfilteredRequestHadBeenMappedBefore = false;
  unfilteredSegmentationDict: SegmentationDict | null = {};

  modelTask = task(
    { restartable: true },
    async (model: Model, params: Params): Promise<void> => {
      await timeout();

      // Fetch favourites
      if (!this.favourites) {
        const requestFavourites = {
          market: this.hotelAndMarketService.market.id,
          page_type: 'RI',
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.store
          .query('insightpagesfavourite', requestFavourites)
          .then((content) => {
            set(this, 'favourites', [...content]);
          });
      }

      const { dateRangeStart, dateRangeEnd } = getDateRange(
        params.fromDate,
        params.toDate,
        this.hotelAndMarketService,
        params.stayDateFilter,
      );

      if (params.segmentationFilters === undefined) {
        params.segmentationFilters = [];
      }

      const segmentationCategoryMappings =
        this.hotelAndMarketService.user.riMpSegmentationCategoryMappings;

      const unfilteredQueryParams: Record<string, unknown> = {
        namespace: APIGATEWAY,
        typeKey: 'revenueinsight/api_reservations/multi_property',
        market_id: this.hotelAndMarketService.market.id,
        start_date: dateRangeStart,
        end_date: dateRangeEnd,
        ref_period: params.reference,
        full_portfolio: params.showFullPortfolio,
        'aggregations[]': this.breakdownsToFetch,
        'category_aggregations[]': segmentationCategoryMappings,
        disable_pickup_ly: params.hidePickupLY,
        kpi_category: params.kpiCategory,
        currency: this.riSelections.riCurrency,
      };

      const extractDate = getExtractDate(
        params.extractDate,
        this.access.isStaffUser,
      );
      if (extractDate) {
        unfilteredQueryParams['extract_date'] = extractDate;
      }

      const filteredQueryParams = {
        ...unfilteredQueryParams,
        filters: params.segmentationFilters,
      };

      if (this.storedUnfilteredResponse) {
        try {
          let result;

          if (params.segmentationFilters.length) {
            const queryResult =
              await this.store.query<ReviMultiPropertyApiResponse>(
                'revi-multi-property-api-response',
                filteredQueryParams,
              );
            result = queryResult?.[0];
          }

          if (result) {
            set(model, 'exchangeRates', result.exchangeRates);
          }

          this.handleUnfilteredAndFilteredRequest(
            this.storedUnfilteredResponse.cachedResponse,
            result?.reservations,
            true,
            model,
            params,
            this.storedUnfilteredResponse.remainingElements,
            result?.remainingElements,
          );
        } catch (error) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          handleRequestError(error, params, this);
        }
      } else {
        const promises = {};

        // @ts-expect-error: Incorrect type
        promises.unfilteredRequest =
          this.store.query<ReviMultiPropertyApiResponse>(
            'revi-multi-property-api-response',
            // @ts-expect-error: Incorrect type
            unfilteredQueryParams,
          );

        if (params.segmentationFilters.length) {
          // @ts-expect-error: Incorrect type
          promises.filteredRequest =
            this.store.query<ReviMultiPropertyApiResponse>(
              'revi-multi-property-api-response',
              filteredQueryParams,
            );
        }

        try {
          const hashResult = await hash(promises).then((result) => ({
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            unfilteredRequest: result.unfilteredRequest?.[0],
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            filteredRequest: result.filteredRequest?.[0],
          }));

          this.storedUnfilteredResponse = {
            hotelInfoId: this.hotelAndMarketService.hotelOrMarket.id!,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            cachedResponse: hashResult.unfilteredRequest.reservations,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            exchangeRates: hashResult.unfilteredRequest.exchangeRates,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            remainingElements: hashResult.unfilteredRequest.remainingElements,
          };

          set(
            model,
            'exchangeRates',
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            hashResult.unfilteredRequest?.exchangeRates,
          );

          this.handleUnfilteredAndFilteredRequest(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            hashResult.unfilteredRequest?.reservations,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            hashResult.filteredRequest?.reservations,
            false,
            model,
            params,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            hashResult.unfilteredRequest?.remainingElements,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            hashResult.filteredRequest?.remainingElements,
          );
        } catch (error) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          handleRequestError(error, params, this);
        }
      }

      this.checkKpiCategory(params.kpiCategory);

      // @ts-expect-error: Incorrect type
      this.lastReturnedSegmentations = model.segmentations;
    },
  );

  requestMarketCurrenciesTask = task(
    { restartable: true },
    async (marketId: string, currency: unknown) => {
      try {
        const currencies = await this.store.query<Currency>('currency', {
          namespace: APIGATEWAY,
          typeKey: `markets/${marketId}/currencies`,
          // @ts-expect-error: Incorrect type
          reference_currency: currency,
        });

        this.cachedMarketCurrencyArgs = { marketId, currency };

        return currencies;
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });

        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  get breakdownsToFetch(): string[] {
    const { riMpSegmentationOrder } = this.hotelAndMarketService.user;

    const orderToUse = riMpSegmentationOrder.length
      ? riMpSegmentationOrder
      : DEFAULT_SEGMENTATION_BLOCKS_ORDER.map(
          (blockOrder) => blockOrder.segmentationKey,
        );

    return [
      ...orderToUse.slice(0, MAX_AMOUNT_OF_BLOCKS),
      RI_BREAKDOWNS.SUBSCRIPTION_ID,
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get defaultSegmentationObjects() {
    const defaultSegmentationObjects = this.breakdownsToFetch.reduce(
      (accumulator, breakdown) => {
        accumulator[breakdown] = [];

        return accumulator;
      },
      {} as Record<string, never[]> & { isLoading: boolean },
    );

    defaultSegmentationObjects.isLoading = true;

    return defaultSegmentationObjects;
  }

  activate(): void {
    this.riSelections.isMultiProperty = true;
  }

  /**
   * This function adds the remaining elements to the filtered dict.
   * It's similar to makeFilteredSegmentationDict but requires fewer edge cases which is why we handle it separately
   * @param { object } filteredResponse : the remaining elements of the unfiltered response
   * @param { object } unfilteredResponse : the remaining elements of the unfiltered response (needed to fill out missing values after filtering)
   * @param { object } params : query parameters
   */

  addRemainingElementsToFilteredDict(
    filteredResponse: ReviRemainingElementsResponse,
    unfilteredResponse: ReviRemainingElementsResponse,
    params: Params,
  ): void {
    const { filteredSegmentationDict } = this;

    if (!filteredSegmentationDict) {
      return;
    }

    // Get the segmentation filters from the params;
    // This is the same as in the riSelections service.
    // I tried using that but it was not yet set there when this is called
    let currentFilters: {
      [key in RiBreakdown]?: string[];
    } = {};

    if (params.segmentationFilters) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      currentFilters = JSON.parse(params.segmentationFilters);
    }

    const { keys } = filteredResponse;

    keys.forEach((key) => {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      filteredResponse[key].forEach((item) => {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.formatRemainingItem(item, key);

        // @ts-expect-error: Incorrect type
        if (filteredSegmentationDict[key]) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          filteredSegmentationDict[key][item.unfilteredId] = item;
        }
      });

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      unfilteredResponse[key].forEach(
        (item: RevenueInsightSegmentationValue | ReviRemainingElement) => {
          addDummyElementToFilteredSegmentationDict(
            filteredSegmentationDict,
            item,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            currentFilters[key],
          );
        },
      );
    });
  }

  /**
   * This function adds the remaining elements to the unfiltered dict.
   * It's similar to makeUnfilteredSegmentationDict but requires fewer edge cases which is why we handle it separately
   */

  addRemainingElementsToUnfilteredDict(
    unfilteredResponse: ReviRemainingElementsResponse,
  ): void {
    const { unfilteredSegmentationDict } = this;

    if (!unfilteredSegmentationDict) {
      return;
    }

    const { keys } = unfilteredResponse;
    keys.forEach((key) => {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      unfilteredResponse[key].forEach((item) => {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.formatRemainingItem(item, key);

        // @ts-expect-error: Incorrect type
        if (unfilteredSegmentationDict[key]) {
          // @ts-expect-error: Type 'null' cannot be used as an index type.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          unfilteredSegmentationDict[key][item.id] = item;
        }
      });
    });
  }

  /*
   * Set defaultSegmentationObject with empty arrays
   */

  beforeModel(transition: Transition): void {
    if (!this.access.hasFeature('app.market.revenue_insight')) {
      this.transition.redirectToEnabledTab(transition);
    } else {
      this.transition.validateRevenueInsightMultiPropertyQueryParams(
        transition,
      );
      this.checkFilterUpdate(transition);
    }
  }

  checkFilterUpdate(transition: Transition): void {
    if (!transition.from || !transition.to) {
      return;
    }

    const isSame = compareQueryParameters(
      transition.from.queryParams as QueryParams,
      transition.to.queryParams as QueryParams,
    );

    const noQueryParamsChanged =
      isSame.currency &&
      isSame.fromDate &&
      isSame.hidePickupLY &&
      isSame.kpiCategory &&
      isSame.reference &&
      isSame.showFullPortfolio &&
      isSame.stayDateFilter &&
      isSame.toDate;

    if (noQueryParamsChanged) {
      return;
    }

    this.clearCache();
  }

  checkKpiCategory(kpiCategory: KpiCategory): void {
    // If there are no available kpi categories, we don't need to check anything
    // This can happen when the API is not yet loaded or failed
    if (this.riSelections.availableKpiCategories.length === 0) {
      return;
    }

    // Check that the selected kpi category is included in the list of kpi categories that we get from API.
    if (!this.riSelections.availableKpiCategories.includes(kpiCategory)) {
      this.router.transitionTo({
        queryParams: { kpiCategory: KPI_CATEGORIES.TOTAL },
      });
      this.lastDisplayedData = null;
    }
  }

  clearCache(): void {
    this.storedUnfilteredResponse = null;
    this.unfilteredSegmentationDict = null;
    this.filteredSegmentationDict = null;
    this.unfilteredRequestHadBeenMappedBefore = false;

    /**
     * In application tests, the app gets torn down after each test. This causes the store to be destroyed
     * before the deactivate hook is called. This check is to prevent the store from being accessed after it's destroyed.
     */
    if (this.store.isDestroyed) {
      return;
    }

    this.store.unloadAll('revi-multi-property-api-response');
    this.store.unloadAll('revi-remaining-elements-response');
    this.store.unloadAll('revi-remaining-element');
    this.store.unloadAll('revi-exchange-rate');
    this.store.unloadAll('revi-api-response');
    this.store.unloadAll('revenue-insight/kpis-request');
    this.store.unloadAll('revenue-insight/breakdowns-request');
    this.store.unloadAll('revenue-insight/segmentation-value');
    this.store.unloadAll('revenue-insight/segmentation-data');
  }

  clearCachedResponse(controller: MarketRevenueInsightController): void {
    set(controller, 'showLoadingPlaceholderState', true);
    set(this, 'lastDisplayedData', null);

    this.clearCache();
  }

  clearFilters(controller: MarketRevenueInsightController): void {
    set(controller, 'segmentationFilters', '');
    set(controller, 'stayDateFilter', '');
  }

  /**
   * Adds UI-specific properties to the remaining item.
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatRemainingItem(item: ReviRemainingElement, segmentationId: RiBreakdown) {
    if (item.segmentationId === segmentationId) {
      return item;
    }

    // These are watched properties and will be set
    set(item, 'isSelectable', false);
    set(item, 'isAggregatedRow', true);
    item.segmentationId = segmentationId;
    item.displayValue = this.intl
      .t('insight_pages.breakdown.more', { count: item.remainingValues.length })
      .toString();

    return item;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getSegmentations() {
    // @ts-expect-error: Incorrect type
    let segmentations = [];

    const { riMpSegmentationOrder } = this.hotelAndMarketService.user;
    if (riMpSegmentationOrder.length) {
      segmentations = [
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        ...segmentations,
        ...riMpSegmentationOrder.slice(0, MAX_AMOUNT_OF_BLOCKS),
      ];
    } else {
      const mappedSegmentations = DEFAULT_SEGMENTATION_BLOCKS_ORDER.slice(
        0,
        MAX_AMOUNT_OF_BLOCKS,
      ).map((block) => block.segmentationKey);

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      segmentations = [...segmentations, ...mappedSegmentations];
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return segmentations;
  }

  handleUnfilteredAndFilteredRequest(
    unfilteredRequest: ReviApiResponseLike,
    filteredRequest: ReviApiResponseLike | undefined,
    isCached: boolean,
    model: { segmentations: unknown },
    params: Params,
    unfilteredRemainingElements?: ReviRemainingElementsResponse,
    filteredRemainingElements?: ReviRemainingElementsResponse,
  ): void {
    const request = filteredRequest ?? unfilteredRequest;

    this.setRiSelectionsPropertiesFromRequest(request);

    if (!isCached) {
      this.makeUnfilteredSegmentationDict(unfilteredRequest);
    }

    const hasFilter = !!filteredRequest;

    if (hasFilter) {
      this.makeFilteredSegmentationDict(filteredRequest, unfilteredRequest);
    }

    // The remaining elements are done after the unfiltered and filtered dict have been created
    // This is done becuase you can filter on something in the remaining values
    // and only if it's already in the filtered dict it should be removed

    // Only for multi property
    if (unfilteredRemainingElements) {
      if (!isCached || hasFilter) {
        this.addRemainingElementsToUnfilteredDict(unfilteredRemainingElements);
      }

      if (filteredRemainingElements) {
        this.addRemainingElementsToFilteredDict(
          filteredRemainingElements,
          unfilteredRemainingElements,
          params,
        );
      }
    }

    const finalResult = this.mapSegmentationItems(hasFilter);

    // If there is no filter, the unfiltered request has just been mapped. Save it for later
    this.unfilteredRequestHadBeenMappedBefore =
      this.unfilteredRequestHadBeenMappedBefore || !hasFilter;

    this.lastDisplayedData = finalResult;

    set(model, 'segmentations', finalResult);
  }

  makeFilteredSegmentationDict(
    filteredResponse: ReviApiResponseLike,
    unfilteredResponse: ReviApiResponseLike,
  ): void {
    const filteredSegmentationDict: SegmentationDict = {};

    const isFuture = this.riSelections.isToDateInFuture;
    const demoMode = getDemoModeCookie();

    // Loop over every key in de model (travel_agent, all, ...)
    const { keys } = filteredResponse;

    keys.forEach((key) => {
      filteredSegmentationDict[key] = {};

      const segmentationIdValidations = getSegmentationIdValidations(key);

      filteredResponse[key].forEach((filteredItem) => {
        const unfilteredItem =
          this.unfilteredSegmentationDict?.[key]?.[filteredItem.unfilteredId];
        if (unfilteredItem) {
          filteredItem.unfiltered_data = unfilteredItem;
          // All of these values are exactly the same as for the unfiltered one
          // No need to calculate them again
          filteredItem.displayValue = unfilteredItem.displayValue;
          filteredItem.segmentationSortValue =
            unfilteredItem.segmentationSortValue;
          filteredItem.pickupCount = unfilteredItem.pickupCount;
          filteredItem.isSelectable = unfilteredItem.isSelectable;
        } else {
          // If there is no unfiltered data, we need to the same for this as when we receive unfiltered data
          // This can happen for the pickup block, or if the values are outside of the top 200 in RIMP
          this.mapNewSegmentationItem(
            filteredItem,
            segmentationIdValidations,
            isFuture,
            demoMode,
          );
        }

        if (filteredSegmentationDict[key]) {
          filteredSegmentationDict[key][filteredItem.unfilteredId] =
            filteredItem;
        }
      });

      // No need for sorting on unfiltered values for these two breakdowns
      if (segmentationIdValidations.isKeyNotPickupAndPace) {
        unfilteredResponse[key].forEach((unfilteredItem) => {
          addDummyElementToFilteredSegmentationDict(
            filteredSegmentationDict,
            unfilteredItem,
          );
        });
      }
    });

    this.filteredSegmentationDict = filteredSegmentationDict;
  }

  makeUnfilteredSegmentationDict(response: ReviApiResponseLike): void {
    const unfilteredSegmentationDict: SegmentationDict = {};

    const isFuture = this.riSelections.isToDateInFuture;
    const demoMode = getDemoModeCookie();

    const { keys } = response;
    keys.forEach((key) => {
      const segmentationIdValidations = getSegmentationIdValidations(key);

      unfilteredSegmentationDict[key] = {};

      response[key].forEach((item) => {
        this.mapNewSegmentationItem(
          item,
          segmentationIdValidations,
          isFuture,
          demoMode,
        );

        if (unfilteredSegmentationDict[key]) {
          // @ts-expect-error: Type 'null' cannot be used as an index type.
          unfilteredSegmentationDict[key][item.id] = item;
        }
      });
    });

    this.unfilteredSegmentationDict = unfilteredSegmentationDict;
  }

  mapNewSegmentationItem(
    item: RevenueInsightSegmentationValue,
    segmentationIdValidations: ReturnType<typeof getSegmentationIdValidations>,
    isFuture: boolean,
    demoMode: DemoModeObject | null,
  ): void {
    const hasNoValueAndNoReferenceValue =
      item.value === null && item.referenceValue === null;

    // segmentationSortValue is different for pickup segmentations, handled in separate if
    if (segmentationIdValidations.isKeyNotPickupAndWeekday) {
      item.segmentationSortValue = item.segmentationValue;
    }

    // Translating the weekdays from 1 - 7 to actual days
    if (segmentationIdValidations.isKeyWeekday) {
      item.segmentationSortValue = item.segmentationValueId;
    }

    if (
      segmentationIdValidations.isKeyPickup &&
      item.segmentationValue.indexOf(
        `revenue_insight.${RI_BREAKDOWNS.PICKUP}`,
      ) !== -1
    ) {
      const splittedSegmentationValue = item.segmentationValue.split('.');
      const xValue = splittedSegmentationValue[2];
      item.pickupCount = xValue;
    }

    if (
      item.segmentationValue.includes('revenue_insight.unaccounted_group') ||
      item.segmentationValue.includes(
        'revenue_insight.unaccounted_transient',
      ) ||
      item.segmentationValue.includes('revenue_insight.left_to_map')
    ) {
      item.isSelectable = false;
    } else {
      item.isSelectable = true;
    }

    const hasInvalidValueAndInvalidReferenceValue =
      item.value?.message === INVALID_RESERVATIONS_MESSAGE &&
      item.referenceValue?.message === INVALID_RESERVATIONS_MESSAGE;

    if (
      segmentationIdValidations.isKeyPickup &&
      (hasNoValueAndNoReferenceValue || hasInvalidValueAndInvalidReferenceValue)
    ) {
      item.isDisabled = true;
    }

    const subscriptionName = item.meta?.name;

    // We only format put the displayValue for breakdowns that can be searched
    // Since pace and stay date cannot be searched, no need for a display value
    // This also makes sure we don't use the moment format in this route which save ~100ms
    if (segmentationIdValidations.isKeyNotPaceAndStayDate) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      item.displayValue = segmentationValueFormatter(
        item.segmentationId,
        item.segmentationValue,
        {
          // Should be no past dates, today is not a past date
          isFuture,
          subscriptionName,
          intl: this.intl,
          demoMode,
        },
      );
    }
  }

  mapSegmentationItems(hasFilter = false): {
    [key in RiBreakdown]?: (SegmentationValueLike | ReviRemainingElement)[];
  } {
    const reservations: {
      [key in RiBreakdown]?: (SegmentationValueLike | ReviRemainingElement)[];
    } = {};

    // determine where you need to get the values from
    let segmentationDict = this.unfilteredSegmentationDict;

    if (hasFilter) {
      segmentationDict = this.filteredSegmentationDict;
    }

    if (!segmentationDict) {
      return {};
    }

    // If it's already been mapped before, no need to map the children again
    // We already saved the mapping in the dict
    const shouldMap = hasFilter || !this.unfilteredRequestHadBeenMappedBefore;

    const keys = Object.keys(
      segmentationDict,
    ) as (keyof typeof segmentationDict)[];

    if (shouldMap) {
      // Reset children properties from the previous mapping
      keys.forEach((segmentationId) => {
        if (segmentationDict[segmentationId]) {
          Object.keys(segmentationDict[segmentationId]).forEach(
            (segmentationValueId) => {
              const segmentationItem =
                segmentationDict[segmentationId]?.[segmentationValueId];
              if (
                segmentationItem &&
                'children' in segmentationItem &&
                segmentationItem?.children?.length
              ) {
                segmentationItem.children = [];
              }
            },
          );
        }
      });
    }

    keys.forEach((segmentName) => {
      reservations[segmentName] = [];
      // Always loop over the unfiltered request. Even though a filter is active, we always show all items of a breakdown
      if (segmentationDict[segmentName]) {
        Object.keys(segmentationDict[segmentName]).forEach(
          (segmentationItemId) => {
            const reservationData =
              segmentationDict[segmentName]?.[segmentationItemId];

            // If pickup is filtered, pace is filtered as well
            // We do not add dummy values for those two so we need to make sure they are in the dict
            if (reservationData) {
              if (shouldMap && reservationData.parentId) {
                const parent =
                  segmentationDict[segmentName]?.[reservationData.parentId];

                if (parent) {
                  // set default
                  if (!parent.children) {
                    parent.children = [];
                  }

                  // hang this child under the parent
                  parent.children.push(
                    reservationData as SegmentationValueLike,
                  );
                  // and a link the other way around to the parent from here
                  reservationData.parent = parent;
                }
              }
              // Only add root elements to the model
              if (reservationData.parentId === null) {
                reservations[segmentName]?.push(reservationData);
              }
            }
          },
        );
      }
    });

    return reservations;
  }

  model(params: Params): Model {
    const model: Model = {
      exchangeRates: null,
      // @ts-expect-error: Incorrect type
      segmentations: deepExtend(this.defaultSegmentationObjects),
    };

    const { segmentations } = model;

    if (this.lastDisplayedData) {
      // setProperties makes sure we don't rerender if it's the same data
      setProperties(segmentations, this.lastDisplayedData);
    }

    const marketId = this.hotelAndMarketService.market.id;
    const currency = this.riSelections.riCurrency;

    const shouldRequestCurrencies =
      marketId !== this.cachedMarketCurrencyArgs?.marketId ||
      currency !== this.cachedMarketCurrencyArgs?.currency;

    // @ts-expect-error: Incorrect type
    model.currenciesTask = this.requestMarketCurrenciesTask;
    if (shouldRequestCurrencies) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      model.currenciesTask.perform(marketId, currency);
    }

    model.modelTask = this.modelTask;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    model.modelTask.perform(model, params);

    return model;
  }

  // @ts-expect-error: Incorrect type

  resetController(
    controller: MarketRevenueInsightController,
    isExiting: boolean,
  ): void {
    if (isExiting) {
      // Clear segmentationFilters & cached data when the route is exiting
      this.clearFilters(controller);
      this.clearCachedResponse(controller);
    }
  }

  /**
   * This function sets the required riSelections properties from the API response.
   * @param { object } request : the request object
   */

  setRiSelectionsPropertiesFromRequest(request: ReviApiResponseLike): void {
    const allSegmentationMeta = request.all[0]?.meta;

    if (!allSegmentationMeta) {
      return;
    }

    this.riSelections.availableKpiCategories =
      allSegmentationMeta.kpi_categories;
    this.riSelections.summaryOverwrite = allSegmentationMeta.uses_summary;
    this.riSelections.isNetComparison = allSegmentationMeta.is_net_comparison;
  }

  @action doRefresh(): void {
    // eslint-disable-next-line ember/no-controller-access-in-routes
    const controller = this.controllerFor(
      'market.revenue-insight',
    ) as unknown as MarketRevenueInsightController;

    this.clearCachedResponse(controller);
    this.refresh();
  }
}
